'use client';

import { Button } from '@/components/ui/button';
import {
  useAvailable,
  useCollateral,
  useMinimum,
  useRate,
  useRecipient,
} from '@/lib/services/sale/service';
import { TranslogChain } from '@/lib/types/translog-chain.type';
import { useState, type ReactElement } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { formatAddress } from '@/lib/utils/format-address';
import { CopyIcon } from 'lucide-react';
import { useCopy } from '@/lib/hooks/use-copy';
import { Skeleton } from '@/components/ui/skeleton';
import { formatEther } from 'viem';
import { localeNumber } from '@/lib/utils/format-number';
import { CHAINS } from '@/lib/constants/chains';
import { useTotal } from '@/lib/services/allocations/service';

function CopyRow({
  value,
  className,
}: {
  readonly value: string;
  readonly className?: string;
}): ReactElement {
  const { copy } = useCopy();

  return (
    <button
      type="button"
      onClick={() => copy(value)}
      className={`flex flex-row items-center gap-1.5 cursor-pointer rounded-md transition-colors hover:bg-accent hover:text-accent-foreground ${className ?? ''}`}
    >
      <span className="truncate">{formatAddress(value)}</span>
      <CopyIcon className="w-4 h-4 shrink-0 opacity-70" />
    </button>
  );
}

export default function Payment({
  options = { available: true, sales: true, volume: true },
}: {
  readonly options?: {
    readonly available: boolean;
    readonly sales: boolean;
    readonly volume: boolean;
  };
}): ReactElement {
  const [chain, setChain] = useState(TranslogChain.BNB);
  const { available } = useAvailable();
  const { collateral } = useCollateral(chain);
  const { recipient } = useRecipient(chain);
  const { rate } = useRate();
  const { minimum } = useMinimum();
  const { total } = useTotal();

  return (
    <div className="w-full max-w-md mx-auto flex flex-col gap-4 rounded-xl border bg-card p-3 sm:p-5">
      <div className="w-full grid grid-cols-3 gap-1.5 sm:gap-2">
        {CHAINS.map(({ chain: c, label, short, Icon }) => (
          <Button
            key={c}
            className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-1.5 h-auto py-2 sm:py-2.5"
            onClick={() => setChain(c)}
            variant={chain === c ? 'default' : 'outline'}
          >
            <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
            <span className="hidden sm:inline text-sm">{label}</span>
            <span className="sm:hidden text-xs">{short}</span>
          </Button>
        ))}
      </div>
      <div className="w-full flex flex-col items-center gap-3 rounded-lg border bg-muted/40 p-4">
        <div className="flex items-center justify-center rounded-md overflow-hidden w-44 h-44 sm:w-52 sm:h-52 bg-white shrink-0 p-2">
          {recipient.data ? (
            <QRCodeSVG value={recipient.data.result} size={208} className="w-full h-full" />
          ) : (
            <LoadingSpinner className="w-8 h-8 sm:w-10 sm:h-10" />
          )}
        </div>
        <p className="text-xs text-muted-foreground text-center">
          Scan the code or copy the address below to send USDT
        </p>
        {recipient.data ? (
          <CopyRow
            value={recipient.data.result}
            className="max-w-full px-2.5 py-1.5 font-mono text-sm"
          />
        ) : (
          <Skeleton className="w-full h-8 max-w-56" />
        )}
      </div>
      <div className="w-full grid grid-cols-2 gap-2.5 sm:gap-3">
        <div className="flex flex-col gap-0.5 rounded-lg border p-3">
          <p className="text-xs text-muted-foreground">Rate</p>
          {rate.data ? (
            <p className="font-semibold text-sm sm:text-base truncate">
              1 USDT = {localeNumber(rate.data.result.formatted, { maximumFractionDigits: 6 })}{' '}
              SOLAR
            </p>
          ) : (
            <Skeleton className="w-full h-5" />
          )}
        </div>
        <div className="flex flex-col gap-0.5 rounded-lg border p-3">
          <p className="text-xs text-muted-foreground">USDT address</p>
          {collateral.data ? (
            <CopyRow
              value={collateral.data.result}
              className="font-semibold text-sm sm:text-base"
            />
          ) : (
            <Skeleton className="w-full h-5" />
          )}
        </div>
        <div className="flex flex-col gap-0.5 rounded-lg border p-3">
          <p className="text-xs text-muted-foreground">Minimum</p>
          {minimum.data ? (
            <p className="font-semibold text-sm sm:text-base truncate">
              {localeNumber(formatEther(BigInt(minimum.data.result)), {
                maximumFractionDigits: 6,
              })}{' '}
              USDT
            </p>
          ) : (
            <Skeleton className="w-full h-5" />
          )}
        </div>
        <div className="flex flex-col gap-0.5 rounded-lg border p-3">
          <p className="text-xs text-muted-foreground">Maximum</p>
          {available.data ? (
            <p className="font-semibold text-sm sm:text-base truncate">
              {localeNumber(formatEther(BigInt(available.data.result.collateral)), {
                maximumFractionDigits: 6,
              })}{' '}
              USDT
            </p>
          ) : (
            <Skeleton className="w-full h-5" />
          )}
        </div>
        {options.available && (
          <div className="flex flex-col gap-0.5 rounded-lg border p-3">
            <p className="text-xs text-muted-foreground">Available</p>
            {available.data ? (
              <p className="font-semibold text-sm sm:text-base truncate">
                {localeNumber(formatEther(BigInt(available.data.result.available)), {
                  maximumFractionDigits: 6,
                })}{' '}
                SOLAR
              </p>
            ) : (
              <Skeleton className="w-full h-5" />
            )}
          </div>
        )}
        {options.sales && (
          <div className="flex flex-col gap-0.5 rounded-lg border p-3">
            <p className="text-xs text-muted-foreground">Total sales</p>
            {total.data ? (
              <p className="font-semibold text-sm sm:text-base truncate">
                {localeNumber(total.data.result.count)}
              </p>
            ) : (
              <Skeleton className="w-full h-5" />
            )}
          </div>
        )}
        {options.volume && (
          <div className="flex flex-col gap-0.5 rounded-lg border p-3 col-span-2">
            <p className="text-xs text-muted-foreground">Total volume</p>
            {total.data ? (
              <p className="font-semibold text-sm sm:text-base truncate">
                {localeNumber(total.data.result.total, { maximumFractionDigits: 6 })} USDT
              </p>
            ) : (
              <Skeleton className="w-full h-5" />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
